import React, { useState, useEffect, useCallback, useRef } from 'react';
import FieldMappingPicker from './components/FieldMappingPicker';

// ─── API helper ───────────────────────────────────────────────────────────────
const API = (path, opts = {}) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const baseHeaders = { 'Content-Type': 'application/json', ...opts.headers };
  if (token) baseHeaders.Authorization = `Bearer ${token}`;
  return fetch(path, { ...opts, headers: baseHeaders });
};

// ─── Element palette ──────────────────────────────────────────────────────────
const PALETTE = [
  { group: 'Layout', icon: '⬜', items: [
    { type: 'columns', icon: '⊞', label: 'Columns' },
    { type: 'tabs', icon: '🗂', label: 'Tabs' },
    { type: 'collapsible', icon: '▶', label: 'Collapsible' },
    { type: 'section', icon: '▬', label: 'Section' },
    { type: 'repeating_group', icon: '↻', label: 'Repeating Group' },
    { type: 'page', icon: '📄', label: 'Page (step)' },
    { type: 'divider', icon: '—', label: 'Divider' },
    { type: 'spacer', icon: '↕', label: 'Spacer' },
  ]},
  { group: 'Display', icon: '🖼', items: [
    { type: 'html', icon: '</>', label: 'HTML Block' },
    { type: 'image_display', icon: '🖼', label: 'Image' },
    { type: 'video_embed', icon: '▶', label: 'Video' },
    { type: 'progress_display', icon: '▓', label: 'Progress' },
  ]},
  { group: 'Text', icon: 'Aa', items: [
    { type: 'text', icon: 'T', label: 'Text' },
    { type: 'email', icon: '@', label: 'Email' },
    { type: 'phone', icon: '☎', label: 'Phone' },
    { type: 'integer', icon: '#', label: 'Integer' },
    { type: 'float', icon: '0.0', label: 'Decimal' },
    { type: 'currency', icon: '$', label: 'Currency' },
    { type: 'percentage', icon: '%', label: 'Percentage' },
    { type: 'password', icon: '🔒', label: 'Password' },
    { type: 'url', icon: '🔗', label: 'URL' },
    { type: 'textarea', icon: '¶', label: 'Textarea' },
    { type: 'rich_text', icon: 'B/', label: 'Rich Text' },
  ]},
  { group: 'Choice', icon: '☑', items: [
    { type: 'select', icon: '▾', label: 'Select' },
    { type: 'multiselect', icon: '☰', label: 'Multi-Select' },
    { type: 'searchable_select', icon: '🔍', label: 'Searchable' },
    { type: 'combobox', icon: '✎', label: 'Combobox' },
    { type: 'radio', icon: '◎', label: 'Radio' },
    { type: 'radio_cards', icon: '▣', label: 'Radio Cards' },
    { type: 'image_choice', icon: '🖼', label: 'Image Choice' },
    { type: 'checkbox', icon: '☑', label: 'Checkbox' },
    { type: 'checkbox_group', icon: '☑☑', label: 'Checkbox Group' },
    { type: 'toggle', icon: '⏻', label: 'Toggle' },
  ]},
  { group: 'Scale', icon: '★', items: [
    { type: 'rating', icon: '★', label: 'Rating' },
    { type: 'slider', icon: '⊸', label: 'Slider' },
    { type: 'range_slider', icon: '⊸⊸', label: 'Range' },
    { type: 'nps', icon: '0→10', label: 'NPS' },
    { type: 'opinion_scale', icon: '1→5', label: 'Opinion Scale' },
    { type: 'matrix', icon: '⊞', label: 'Matrix/Likert' },
  ]},
  { group: 'Date & Time', icon: '📅', items: [
    { type: 'date', icon: '📅', label: 'Date' },
    { type: 'time', icon: '🕐', label: 'Time' },
    { type: 'datetime', icon: '⌚', label: 'Date+Time' },
    { type: 'date_range', icon: '📆', label: 'Date Range' },
  ]},
  { group: 'Files', icon: '📎', items: [
    { type: 'file', icon: '📎', label: 'File Upload' },
    { type: 'image', icon: '🖼', label: 'Image Upload' },
  ]},
  { group: 'Special', icon: '✦', items: [
    { type: 'address', icon: '📍', label: 'Address' },
    { type: 'name', icon: '👤', label: 'Name' },
    { type: 'color', icon: '🎨', label: 'Color Picker' },
    { type: 'map_picker', icon: '🗺', label: 'Map Picker' },
    { type: 'signature', icon: '✍', label: 'Signature' },
    { type: 'lookup', icon: '🔎', label: 'Lookup' },
    { type: 'hidden', icon: '👁', label: 'Hidden' },
    { type: 'readonly', icon: '🔐', label: 'Read Only' },
    { type: 'captcha', icon: '🤖', label: 'Captcha' },
  ]},
];

const LAYOUT_TYPES = new Set(['columns', 'tabs', 'collapsible', 'section', 'repeating_group', 'page', 'divider', 'spacer', 'html', 'image_display', 'video_embed', 'progress_display']);

function makeId() { return 'el_' + Math.random().toString(36).slice(2, 8); }

// Insert newElem before first item if afterId is null, or after item with afterId
function insertAfterInArray(arr, afterId, newElem) {
  if (!afterId) return [newElem, ...(arr || [])];
  const idx = (arr || []).findIndex(e => e.id === afterId);
  if (idx === -1) return [...(arr || []), newElem];
  const next = [...arr];
  next.splice(idx + 1, 0, newElem);
  return next;
}

function defaultElement(type) {
  const base = { id: makeId(), type, width: 'full', conditionals: [], validation: {} };
  const fieldBase = { ...base, name: type + '_' + makeId().slice(3), label: PALETTE.flatMap(g => g.items).find(i => i.type === type)?.label || type, helpText: '', tooltip: '', required: false, placeholder: '' };
  switch (type) {
    case 'columns': return { ...base, columns: [{ id: makeId(), span: 6, elements: [] }, { id: makeId(), span: 6, elements: [] }], gap: 16 };
    case 'tabs': return { ...base, tabs: [{ id: makeId(), label: 'Tab 1', elements: [] }, { id: makeId(), label: 'Tab 2', elements: [] }] };
    case 'collapsible': return { ...base, label: 'Section', defaultOpen: true, elements: [] };
    case 'section': return { ...base, label: 'Section', description: '', elements: [] };
    case 'repeating_group': return { ...base, label: 'Repeating Group', rowLabel: 'Row', minRows: 1, maxRows: 10, addLabel: '+ Add row', elements: [] };
    case 'page': return { ...base, label: 'Step 1', description: '', elements: [] };
    case 'divider': return { ...base, label: '' };
    case 'spacer': return { ...base, height: 24 };
    case 'html': return { ...base, content: '<p>Enter HTML content here</p>' };
    case 'image_display': return { ...base, src: '', alt: '', width: '100%', alignment: 'center' };
    case 'video_embed': return { ...base, url: '', title: '' };
    case 'progress_display': return { ...base };
    case 'select': return { ...fieldBase, options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }], multiple: false, searchable: false, clearable: true };
    case 'multiselect': return { ...fieldBase, options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }], maxSelections: null, searchable: true };
    case 'searchable_select': return { ...fieldBase, options: [], minChars: 1, debounceMs: 300 };
    case 'combobox': return { ...fieldBase, options: [], allowCustomValue: true };
    case 'radio': return { ...fieldBase, options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }], layout: 'vertical' };
    case 'radio_cards': return { ...fieldBase, options: [{ label: 'Option 1', value: 'option_1', description: '' }, { label: 'Option 2', value: 'option_2', description: '' }], layout: 'horizontal' };
    case 'image_choice': return { ...fieldBase, options: [{ label: 'Choice 1', value: 'choice_1', imageUrl: '' }], multiple: false };
    case 'checkbox': return { ...fieldBase };
    case 'checkbox_group': return { ...fieldBase, options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }], layout: 'vertical' };
    case 'toggle': return { ...fieldBase, onLabel: 'Yes', offLabel: 'No', onValue: 'true', offValue: 'false' };
    case 'rating': return { ...fieldBase, max: 5, icon: 'star', allowHalf: false };
    case 'slider': return { ...fieldBase, min: 0, max: 100, step: 1, showValue: true };
    case 'range_slider': return { ...fieldBase, min: 0, max: 100, step: 1 };
    case 'nps': return { ...fieldBase, lowLabel: 'Not likely', highLabel: 'Very likely' };
    case 'opinion_scale': return { ...fieldBase, min: 1, max: 5, minLabel: 'Strongly disagree', maxLabel: 'Strongly agree', showNumbers: true };
    case 'matrix': return { ...fieldBase, rows: [{ id: makeId(), label: 'Row 1' }, { id: makeId(), label: 'Row 2' }], columns: [{ id: makeId(), label: 'Col 1' }, { id: makeId(), label: 'Col 2' }], multiple: false };
    case 'date': return { ...fieldBase, format: 'YYYY-MM-DD', disabledDates: [] };
    case 'time': return { ...fieldBase, format24h: true, step: 30 };
    case 'datetime': return { ...fieldBase };
    case 'date_range': return { ...fieldBase };
    case 'file': return { ...fieldBase, accept: '*/*', maxSizeMb: 10, multiple: false, dropzoneLabel: 'Click or drag files here' };
    case 'image': return { ...fieldBase, accept: 'image/*', maxSizeMb: 5, preview: true, crop: false, aspectRatio: null };
    case 'signature': return { ...fieldBase, strokeColor: '#000000', strokeWidth: 2, bgColor: '#ffffff' };
    case 'address': return { ...fieldBase, fields: ['street', 'city', 'state', 'zip', 'country'], required_sub_fields: ['street', 'city'] };
    case 'name': return { ...fieldBase, fields: ['first', 'last'], required_sub_fields: ['first', 'last'] };
    case 'color': return { ...fieldBase, format: 'hex', presets: [] };
    case 'map_picker': return { ...fieldBase, defaultLat: 40.7128, defaultLng: -74.006, zoom: 12 };
    case 'lookup': return { ...fieldBase, dataSource: { type: 'rest', url: '', method: 'GET', headers: {}, resultPath: '', valueField: 'id', labelField: 'name' }, displayFields: [], bindTo: [] };
    case 'hidden': return { ...fieldBase, valueMode: 'static', staticValue: '', derivedExpression: '' };
    case 'readonly': return { ...fieldBase, valueMode: 'static', staticValue: '', derivedExpression: '', displayAs: 'text' };
    case 'captcha': return { ...base, provider: 'honeypot' };
    case 'integer': return { ...fieldBase, validation: {}, prefix: '', suffix: '' };
    case 'float': case 'decimal': return { ...fieldBase, decimalPlaces: 2, prefix: '', suffix: '', thousandsSeparator: true };
    case 'currency': return { ...fieldBase, currencyCode: 'USD', locale: 'en-US' };
    case 'percentage': return { ...fieldBase, decimalPlaces: 0, validation: { min: { value: 0 }, max: { value: 100 } } };
    case 'password': return { ...fieldBase, showStrengthMeter: false, confirmField: false, strengthRules: { minLength: 8 } };
    default: return fieldBase;
  }
}

// ─── Shared styles (module-level so sub-components can use them) ──────────────
const propInputStyle = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 12 };
const propSelectStyle = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 12 };

// ─── ElementPreview ───────────────────────────────────────────────────────────
function ElementPreview({ elem, selected, onClick, onDelete, dragHandleProps }) {
  const borderColor = selected ? 'var(--accent-color, #6366f1)' : 'transparent';
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-panel)', color: 'var(--xfd-muted)', fontSize: 13 };

  const renderFieldPreview = () => {
    switch (elem.type) {
      case 'text': case 'email': case 'phone': case 'url': case 'password':
        return <input disabled placeholder={elem.placeholder || elem.label || '…'} style={inputStyle} />;
      case 'integer': case 'float': case 'decimal': case 'currency': case 'percentage':
        return <input disabled type="number" placeholder={elem.placeholder || '0'} style={inputStyle} />;
      case 'textarea': case 'rich_text':
        return <textarea disabled placeholder={elem.placeholder || '…'} rows={3} style={{ ...inputStyle, resize: 'none' }} />;
      case 'select': case 'multiselect': case 'searchable_select': case 'combobox':
        return <select disabled style={inputStyle}><option>{elem.options?.[0]?.label || 'Select…'}</option></select>;
      case 'radio': case 'radio_cards':
        return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{(elem.options || []).slice(0, 3).map(o => <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--xfd-muted)' }}><input type="radio" disabled /> {o.label}</label>)}</div>;
      case 'checkbox': return <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--xfd-muted)' }}><input type="checkbox" disabled /> {elem.label}</label>;
      case 'checkbox_group':
        return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{(elem.options || []).slice(0, 3).map(o => <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--xfd-muted)' }}><input type="checkbox" disabled /> {o.label}</label>)}</div>;
      case 'toggle': return <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><div style={{ width: 36, height: 20, borderRadius: 10, background: 'var(--xfd-border)' }} /><span style={{ fontSize: 12, color: 'var(--xfd-dim)' }}>{elem.offLabel || 'No'}</span></div>;
      case 'date': return <input disabled type="date" style={inputStyle} />;
      case 'time': return <input disabled type="time" style={inputStyle} />;
      case 'datetime': return <input disabled type="datetime-local" style={inputStyle} />;
      case 'date_range': return <div style={{ display: 'flex', gap: 6 }}><input disabled type="date" style={{ ...inputStyle, flex: 1 }} /><span style={{ color: 'var(--xfd-dim)', fontSize: 12, alignSelf: 'center' }}>→</span><input disabled type="date" style={{ ...inputStyle, flex: 1 }} /></div>;
      case 'rating':
        return <div style={{ display: 'flex', gap: 4 }}>{Array.from({ length: elem.max || 5 }).map((_, i) => <span key={i} style={{ fontSize: 18, color: i < 3 ? '#f59e0b' : 'var(--xfd-border)' }}>{elem.icon === 'heart' ? '♥' : '★'}</span>)}</div>;
      case 'slider': return <input disabled type="range" min={elem.min || 0} max={elem.max || 100} defaultValue={50} style={{ width: '100%' }} />;
      case 'range_slider': return <input disabled type="range" min={elem.min || 0} max={elem.max || 100} style={{ width: '100%' }} />;
      case 'nps': return <div style={{ display: 'flex', gap: 3 }}>{Array.from({ length: 11 }, (_, i) => <div key={i} style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--xfd-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--xfd-muted)' }}>{i}</div>)}</div>;
      case 'opinion_scale': return <div style={{ display: 'flex', gap: 4 }}>{Array.from({ length: (elem.max || 5) - (elem.min || 1) + 1 }, (_, i) => <div key={i} style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--xfd-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--xfd-muted)' }}>{(elem.min || 1) + i}</div>)}</div>;
      case 'file': case 'image': return <div style={{ border: '2px dashed var(--xfd-border)', borderRadius: 6, padding: '12px 16px', textAlign: 'center', color: 'var(--xfd-dim)', fontSize: 12 }}>{elem.dropzoneLabel || 'Click or drag to upload'}</div>;
      case 'signature': return <div style={{ border: '1px solid var(--xfd-border)', borderRadius: 6, height: 60, background: 'var(--xfd-panel)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--xfd-dim)', fontSize: 12 }}>✍ Signature pad</div>;
      case 'address': return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>{(elem.fields || ['street', 'city']).slice(0, 4).map(f => <input key={f} disabled placeholder={f.charAt(0).toUpperCase() + f.slice(1)} style={inputStyle} />)}</div>;
      case 'color': return <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input disabled type="color" style={{ width: 36, height: 30, borderRadius: 4, border: 'none' }} /><input disabled placeholder="#6366f1" style={{ ...inputStyle, width: 100 }} /></div>;
      case 'hidden': return <div style={{ border: '1px dashed var(--xfd-border)', borderRadius: 4, padding: '6px 10px', color: 'var(--xfd-dim)', fontSize: 11, fontFamily: 'monospace' }}>hidden: {elem.staticValue || elem.derivedExpression || '(not set)'}</div>;
      case 'readonly': return <div style={{ background: 'var(--xfd-panel)', borderRadius: 6, padding: '8px 12px', color: 'var(--xfd-muted)', fontSize: 13, border: '1px solid var(--xfd-border)' }}>{elem.staticValue || elem.derivedExpression || '(read-only value)'}</div>;
      case 'html': return <div style={{ border: '1px dashed var(--xfd-border)', borderRadius: 4, padding: '6px 10px', color: 'var(--xfd-dim)', fontSize: 11 }} dangerouslySetInnerHTML={{ __html: elem.content || '<i>HTML block</i>' }} />;
      case 'image_display': return <div style={{ textAlign: elem.alignment || 'center', color: 'var(--xfd-dim)', fontSize: 12 }}>{elem.src ? <img src={elem.src} alt={elem.alt} style={{ maxWidth: '100%', maxHeight: 80, borderRadius: 4 }} /> : '🖼 Image'}</div>;
      case 'video_embed': return <div style={{ border: '1px dashed var(--xfd-border)', borderRadius: 4, padding: '12px', textAlign: 'center', color: 'var(--xfd-dim)', fontSize: 12 }}>▶ Video: {elem.url || '(no URL)'}</div>;
      case 'progress_display': return <div style={{ height: 6, background: 'var(--xfd-border)', borderRadius: 3 }}><div style={{ width: '45%', height: '100%', background: 'var(--accent-color, #6366f1)', borderRadius: 3 }} /></div>;
      case 'divider': return <hr style={{ border: 'none', borderTop: '1px solid var(--xfd-border)', margin: '4px 0' }} />;
      case 'spacer': return <div style={{ height: Math.min(elem.height || 24, 40), background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(99,102,241,0.05) 4px, rgba(99,102,241,0.05) 8px)', borderRadius: 4 }} />;
      case 'captcha': return <div style={{ border: '1px solid var(--xfd-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--xfd-dim)' }}>🤖 Spam protection (honeypot)</div>;
      default: return <div style={{ background: 'var(--xfd-border)', borderRadius: 4, height: 32, display: 'flex', alignItems: 'center', padding: '0 10px', color: 'var(--xfd-muted)', fontSize: 12 }}>{elem.type}</div>;
    }
  };

  const hasLabel = !LAYOUT_TYPES.has(elem.type) && elem.type !== 'captcha';
  return (
    <div
      onClick={e => { e.stopPropagation(); onClick(elem); }}
      style={{ position: 'relative', marginBottom: 8, border: `2px solid ${borderColor}`, borderRadius: 8, padding: '10px 12px', background: selected ? 'rgba(99,102,241,0.05)' : 'var(--xfd-subtle)', cursor: 'pointer', transition: 'border-color 0.15s' }}
    >
      <div style={{ position: 'absolute', top: 6, right: 8, display: 'flex', gap: 4, opacity: selected ? 1 : 0, transition: 'opacity 0.15s', zIndex: 2 }}>
        <div {...dragHandleProps} title="Drag to reorder" style={{ cursor: 'grab', padding: '2px 5px', fontSize: 12, color: 'var(--xfd-dim)', userSelect: 'none' }}>⠿</div>
        <button onClick={e => { e.stopPropagation(); onDelete(elem.id); }} style={{ padding: '2px 6px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>×</button>
      </div>
      {hasLabel && !['divider', 'spacer', 'progress_display'].includes(elem.type) && (
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--xfd-text)', marginBottom: 5, display: 'flex', gap: 4, alignItems: 'center' }}>
          {elem.label || '(no label)'}
          {elem.required && <span style={{ color: 'var(--accent-color, #6366f1)', fontSize: 11 }}>*</span>}
          {elem.conditionals?.length > 0 && <span title="Has conditional logic" style={{ fontSize: 10, color: 'var(--xfd-dim)' }}>⚡</span>}
        </div>
      )}
      {renderFieldPreview()}
    </div>
  );
}

// ─── DropZone ─────────────────────────────────────────────────────────────────
function DropZone({ onDrop }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragLeave={e => { e.stopPropagation(); setOver(false); }}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); setOver(false); onDrop(); }}
      style={{ height: over ? 40 : 8, background: over ? 'rgba(99,102,241,0.3)' : 'transparent', borderRadius: 4, border: over ? '2px dashed var(--accent-color, #6366f1)' : '2px dashed transparent', transition: 'all 0.15s', margin: '2px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {over && <span style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)' }}>Drop here</span>}
    </div>
  );
}

// ─── ColumnDropArea ───────────────────────────────────────────────────────────
function ColumnDropArea({ col, colIdx, makeColInsertFn, handleDrop, renderElements, depth }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={e => { e.preventDefault(); setOver(false); const lastId = col.elements[col.elements.length - 1]?.id ?? null; handleDrop(makeColInsertFn(lastId)); }}
      style={{ minHeight: 48, background: over ? 'rgba(99,102,241,0.1)' : 'var(--xfd-subtle)', borderRadius: 6, padding: 6, border: over ? '2px dashed var(--accent-color,#6366f1)' : '1px dashed #1e3a5f', transition: 'background 0.15s, border 0.15s' }}
    >
      <div style={{ fontSize: 10, color: 'var(--xfd-border-hover)', marginBottom: 4, textAlign: 'center' }}>Col {colIdx + 1}</div>
      {renderElements(col.elements, makeColInsertFn, depth + 1)}
    </div>
  );
}

// ─── PreviewTabsBlock ──────────────────────────────────────────────────────────
function PreviewTabsBlock({ elem, renderFn }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeTab = (elem.tabs || [])[activeIdx];
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderBottom: '1px solid var(--xfd-border)' }}>
        {(elem.tabs || []).map((t, i) => (
          <span
            key={t.id}
            onClick={() => setActiveIdx(i)}
            style={{ fontSize: 12, color: i === activeIdx ? 'var(--accent-color,#6366f1)' : 'var(--xfd-dim)', borderBottom: i === activeIdx ? '2px solid var(--accent-color,#6366f1)' : '2px solid transparent', padding: '4px 12px', marginBottom: -1, fontWeight: i === activeIdx ? 600 : 400, cursor: 'pointer', userSelect: 'none' }}
          >
            {t.label}
          </span>
        ))}
      </div>
      {activeTab && renderFn(activeTab.elements)}
    </div>
  );
}

// ─── OptionsEditor (top-level to avoid focus loss) ────────────────────────────
function OptionsEditor({ elem, onChange }) {
  const [text, setText] = useState('');

  // Sync from elem.options whenever the element changes
  useEffect(() => {
    setText((elem.options || []).map(o => o.label + (o.value !== o.label ? '|' + o.value : '')).join('\n'));
  }, [elem.id]); // only reset when switching to a different element

  const apply = () => {
    const opts = text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
      const [label, value] = l.split('|');
      return { label: label.trim(), value: (value || label).trim() };
    });
    onChange({ ...elem, options: opts });
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>Options (one per line; Label|value)</label>
      <textarea value={text} onChange={e => setText(e.target.value)} onBlur={apply} rows={5} style={{ ...propInputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
    </div>
  );
}

// ─── DataSourceEditor (top-level) ─────────────────────────────────────────────
function DataSourceEditor({ elem, allFields, onChange }) {
  const ds = elem.dataSource || {};
  const setDs = (k, v) => onChange({ ...elem, dataSource: { ...ds, [k]: v } });

  return (
    <div style={{ marginTop: 12, padding: 10, background: 'rgba(99,102,241,0.05)', borderRadius: 8, border: '1px solid rgba(99,102,241,0.2)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8 }}>Data Source</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {['static', 'rest', 'soap'].map(t => (
          <button key={t} onClick={() => setDs('type', t)} style={{ flex: 1, padding: '4px 8px', borderRadius: 5, border: 'none', background: ds.type === t ? 'var(--accent-color, #6366f1)' : 'var(--xfd-border)', color: ds.type === t ? '#fff' : 'var(--xfd-muted)', cursor: 'pointer', fontSize: 11, fontWeight: 500, textTransform: 'uppercase' }}>{t}</button>
        ))}
      </div>
      {ds.type !== 'static' && (
        <>
          <input placeholder="URL (use {{fieldName}} tokens)" value={ds.url || ''} onChange={e => setDs('url', e.target.value)} style={{ ...propInputStyle, marginBottom: 6 }} />
          {ds.type === 'rest' && (
            <>
              <select value={ds.method || 'GET'} onChange={e => setDs('method', e.target.value)} style={{ ...propSelectStyle, marginBottom: 6 }}>
                {['GET', 'POST'].map(m => <option key={m}>{m}</option>)}
              </select>
              <input placeholder="Result path (e.g. data.items)" value={ds.resultPath || ''} onChange={e => setDs('resultPath', e.target.value)} style={{ ...propInputStyle, marginBottom: 6 }} />
            </>
          )}
          {ds.type === 'soap' && (
            <input placeholder="SOAPAction" value={ds.soapAction || ''} onChange={e => setDs('soapAction', e.target.value)} style={{ ...propInputStyle, marginBottom: 6 }} />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <input placeholder="Value field" value={ds.valueField || ''} onChange={e => setDs('valueField', e.target.value)} style={propInputStyle} />
            <input placeholder="Label field" value={ds.labelField || ''} onChange={e => setDs('labelField', e.target.value)} style={propInputStyle} />
          </div>
          <div style={{ marginTop: 6 }}>
            <label style={{ fontSize: 11, color: 'var(--xfd-dim)', display: 'block', marginBottom: 3 }}>Depends on field</label>
            <select value={ds.dependsOn || ''} onChange={e => setDs('dependsOn', e.target.value || null)} style={propSelectStyle}>
              <option value="">— None —</option>
              {(allFields || []).filter(f => f.id !== elem.id).map(f => <option key={f.id} value={f.name}>{f.label || f.name}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

// ─── ValidationEditor (top-level) ─────────────────────────────────────────────
function ValidationEditor({ elem, allFields, onChange }) {
  const v = elem.validation || {};
  const isNumeric = ['integer', 'float', 'decimal', 'currency', 'percentage', 'slider', 'range_slider'].includes(elem.type);
  const isText = ['text', 'email', 'phone', 'url', 'password', 'textarea', 'rich_text'].includes(elem.type);
  const hasOptions = ['select', 'multiselect', 'searchable_select', 'combobox', 'radio', 'radio_cards', 'checkbox_group', 'image_choice'].includes(elem.type);

  const setV = (k, val) => onChange({ ...elem, validation: { ...v, [k]: val } });
  const toggleRule = (k) => {
    if (v[k]) { const n = { ...v }; delete n[k]; onChange({ ...elem, validation: n }); }
    else setV(k, { value: '', message: '' });
  };
  const setMsg = (k, msg) => setV(k, { ...v[k], message: msg });
  const setRuleVal = (k, val) => setV(k, { ...v[k], value: val });

  const rules = [];
  if (isText) rules.push(['minLength', 'Min length'], ['maxLength', 'Max length'], ['pattern', 'Regex pattern'], ['notEmpty', 'Not empty']);
  if (elem.type === 'email') rules.push(['email', 'Email format']);
  if (elem.type === 'url') rules.push(['url', 'URL format']);
  if (isNumeric) rules.push(['min', 'Min value'], ['max', 'Max value'], ['minExclusive', 'Min (exclusive)'], ['maxExclusive', 'Max (exclusive)'], ['positive', 'Positive only'], ['negative', 'Negative only'], ['notZero', 'Cannot be zero'], ['integer', 'Whole number only']);
  if (['float', 'decimal', 'currency', 'percentage'].includes(elem.type)) rules.push(['decimalPlaces', 'Max decimal places']);
  if (['date', 'datetime', 'date_range'].includes(elem.type)) rules.push(['minDate', 'Min date'], ['maxDate', 'Max date']);
  if (['file', 'image'].includes(elem.type)) rules.push(['maxSizeMb', 'Max size (MB)'], ['maxFiles', 'Max files']);
  if (hasOptions) rules.push(['minSelections', 'Min selections'], ['maxSelections', 'Max selections']);
  rules.push(['mustMatch', 'Must match field'], ['greaterThan', 'Greater than field'], ['lessThan', 'Less than field'], ['uniqueCheck', 'Unique (API check)']);

  const noValueRules = new Set(['notEmpty', 'email', 'url', 'positive', 'negative', 'notZero', 'integer']);
  const fieldRules = new Set(['mustMatch', 'greaterThan', 'lessThan']);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8 }}>Validation Rules</div>
      {rules.map(([k, label]) => (
        <div key={k} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={!!v[k]} onChange={() => toggleRule(k)} id={`vrule_${elem.id}_${k}`} />
            <label htmlFor={`vrule_${elem.id}_${k}`} style={{ fontSize: 12, color: 'var(--xfd-text)', cursor: 'pointer', flex: 1 }}>{label}</label>
          </div>
          {v[k] && (
            <div style={{ marginLeft: 20, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {!noValueRules.has(k) && (
                fieldRules.has(k) ? (
                  <select value={v[k]?.field || ''} onChange={e => setV(k, { ...v[k], field: e.target.value })} style={{ ...propSelectStyle, fontSize: 11 }}>
                    <option value="">— Pick field —</option>
                    {(allFields || []).filter(f => f.id !== elem.id).map(f => <option key={f.id} value={f.name}>{f.label || f.name}</option>)}
                  </select>
                ) : k === 'uniqueCheck' ? (
                  <input placeholder="API URL" value={v[k]?.url || ''} onChange={e => setV(k, { ...v[k], url: e.target.value })} style={{ ...propInputStyle, fontSize: 11 }} />
                ) : (
                  <input placeholder="Value" value={v[k]?.value ?? ''} onChange={e => setRuleVal(k, e.target.value)} style={{ ...propInputStyle, fontSize: 11 }} />
                )
              )}
              <input placeholder="Custom error message (optional)" value={v[k]?.message || ''} onChange={e => setMsg(k, e.target.value)} style={{ ...propInputStyle, fontSize: 11 }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── ConditionalsEditor (top-level, has own state) ────────────────────────────
function ConditionalsEditor({ elem, allFields, onChange }) {
  const conds = elem.conditionals || [];
  const actions = ['show', 'hide', 'require', 'readonly', 'setLabel'];
  const [activeAction, setActiveAction] = useState(actions[0]);

  // Reset active action when switching elements
  useEffect(() => { setActiveAction(actions[0]); }, [elem.id]);

  const cond = conds.find(c => c.action === activeAction) || { action: activeAction, operator: 'and', rules: [], labelTemplate: '' };
  const enabled = !!conds.find(c => c.action === activeAction);
  const OPS = ['eq', 'neq', 'contains', 'startsWith', 'gt', 'lt', 'gte', 'lte', 'empty', 'notEmpty'];

  const updateCond = (updates) => {
    const updated = { ...cond, ...updates };
    const others = conds.filter(c => c.action !== activeAction);
    onChange({ ...elem, conditionals: enabled ? [...others, updated] : others });
  };

  const toggle = () => {
    if (enabled) onChange({ ...elem, conditionals: conds.filter(c => c.action !== activeAction) });
    else onChange({ ...elem, conditionals: [...conds, { action: activeAction, operator: 'and', rules: [{ field: '', op: 'eq', value: '' }] }] });
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--xfd-border)', paddingTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8 }}>Conditional Logic</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {actions.map(a => (
          <button key={a} onClick={() => setActiveAction(a)} style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: activeAction === a ? 'var(--accent-color, #6366f1)' : 'var(--xfd-border)', color: activeAction === a ? '#fff' : 'var(--xfd-dim)', cursor: 'pointer', fontSize: 11 }}>
            {a === 'setLabel' ? 'Label' : a.charAt(0).toUpperCase() + a.slice(1)}
            {conds.find(c => c.action === a) && <span style={{ marginLeft: 3 }}>●</span>}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input type="checkbox" checked={enabled} onChange={toggle} id={`cond_enable_${elem.id}`} />
        <label htmlFor={`cond_enable_${elem.id}`} style={{ fontSize: 12, color: 'var(--xfd-text)', cursor: 'pointer' }}>Enable {activeAction}</label>
        {enabled && (
          <select value={cond.operator} onChange={e => updateCond({ operator: e.target.value })} style={{ ...propSelectStyle, width: 60, fontSize: 11, marginLeft: 'auto' }}>
            <option value="and">AND</option><option value="or">OR</option>
          </select>
        )}
      </div>
      {enabled && (
        <>
          {(cond.rules || []).map((rule, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
              <select value={rule.field || ''} onChange={e => { const r = [...cond.rules]; r[i] = { ...r[i], field: e.target.value }; updateCond({ rules: r }); }} style={{ ...propSelectStyle, flex: 2, fontSize: 11 }}>
                <option value="">Field…</option>
                {(allFields || []).map(f => <option key={f.id} value={f.name}>{f.label || f.name}</option>)}
              </select>
              <select value={rule.op || 'eq'} onChange={e => { const r = [...cond.rules]; r[i] = { ...r[i], op: e.target.value }; updateCond({ rules: r }); }} style={{ ...propSelectStyle, flex: 1, fontSize: 11 }}>
                {OPS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {!['empty', 'notEmpty'].includes(rule.op) && (
                <input value={rule.value || ''} onChange={e => { const r = [...cond.rules]; r[i] = { ...r[i], value: e.target.value }; updateCond({ rules: r }); }} placeholder="value" style={{ ...propInputStyle, flex: 2, fontSize: 11 }} />
              )}
              <button onClick={() => { const r = cond.rules.filter((_, j) => j !== i); updateCond({ rules: r }); }} style={{ padding: '3px 6px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>×</button>
            </div>
          ))}
          <button onClick={() => updateCond({ rules: [...(cond.rules || []), { field: '', op: 'eq', value: '' }] })} style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add rule</button>
          {activeAction === 'setLabel' && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 11, color: 'var(--xfd-dim)', display: 'block', marginBottom: 3 }}>Label template</label>
              <input value={cond.labelTemplate || ''} onChange={e => updateCond({ labelTemplate: e.target.value })} placeholder="e.g. Zip Code ({{state}})" style={propInputStyle} />
              <div style={{ fontSize: 10, color: 'var(--xfd-dim)', marginTop: 3 }}>Available: {(allFields || []).map(f => `{{${f.name}}}`).join(', ')}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── PropsPanel ───────────────────────────────────────────────────────────────
function PropsPanel({ elem, allFields, onChange, fieldCatalog, caseManagerForm }) {
  if (!elem) return (
    <div style={{ padding: 24, textAlign: 'center', color: 'var(--xfd-dim)', fontSize: 13 }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>✦</div>
      Click an element to edit its properties
    </div>
  );

  const set = (key, val) => onChange({ ...elem, [key]: val });
  const setVal = (key) => (e) => set(key, e.target.value);
  const setCheck = (key) => (e) => set(key, e.target.checked);

  const isField = !LAYOUT_TYPES.has(elem.type);
  const hasOptions = ['select', 'multiselect', 'searchable_select', 'combobox', 'radio', 'radio_cards', 'checkbox_group', 'image_choice'].includes(elem.type);

  const inp = (label, key, type = 'text', extra = {}) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>{label}</label>
      <input type={type} value={elem[key] ?? ''} onChange={setVal(key)} style={propInputStyle} {...extra} />
    </div>
  );

  const chk = (label, key) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <input type="checkbox" checked={!!elem[key]} onChange={setCheck(key)} id={`chk_${elem.id}_${key}`} />
      <label htmlFor={`chk_${elem.id}_${key}`} style={{ fontSize: 12, color: 'var(--xfd-text)', cursor: 'pointer' }}>{label}</label>
    </div>
  );

  const sel = (label, key, options) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>{label}</label>
      <select value={elem[key] ?? ''} onChange={setVal(key)} style={propSelectStyle}>
        {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  // Comma-separated string array input (for case-manager rules).
  const csvIn = (label, key, helpText) => (
    <div style={{ marginBottom: 8 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>{label}</label>
      <input
        type="text"
        value={Array.isArray(elem[key]) ? elem[key].join(', ') : ''}
        onChange={(e) => {
          const arr = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
          set(key, arr.length ? arr : undefined);
        }}
        placeholder="comma, separated"
        style={propInputStyle}
      />
      {helpText && <div style={{ fontSize: 10, color: 'var(--xfd-dim)', marginTop: 2 }}>{helpText}</div>}
    </div>
  );

  // Type-aware default value input
  const defaultValueInput = () => {
    const exclude = ['checkbox', 'toggle', 'rating', 'slider', 'range_slider', 'nps', 'opinion_scale', 'matrix', 'signature', 'color', 'map_picker', 'hidden', 'readonly', 'captcha', 'address', 'name', 'file', 'image', 'html', 'image_display', 'video_embed', 'progress_display', 'divider', 'spacer'];
    if (exclude.includes(elem.type)) return null;
    const labelEl = <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>Default value</label>;
    const wrap = (input) => <div style={{ marginBottom: 10 }}>{labelEl}{input}</div>;

    if (elem.type === 'date') return wrap(<input type="date" value={elem.defaultValue || ''} onChange={setVal('defaultValue')} style={propInputStyle} />);
    if (elem.type === 'time') return wrap(<input type="time" value={elem.defaultValue || ''} onChange={setVal('defaultValue')} style={propInputStyle} />);
    if (elem.type === 'datetime') return wrap(<input type="datetime-local" value={elem.defaultValue || ''} onChange={setVal('defaultValue')} style={propInputStyle} />);
    if (['integer', 'float', 'decimal', 'currency', 'percentage'].includes(elem.type)) return wrap(<input type="number" value={elem.defaultValue ?? ''} onChange={setVal('defaultValue')} style={propInputStyle} />);
    if (elem.type === 'checkbox') return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><input type="checkbox" checked={!!elem.defaultValue} onChange={e => set('defaultValue', e.target.checked)} id={`dv_${elem.id}`} /><label htmlFor={`dv_${elem.id}`} style={{ fontSize: 12, color: 'var(--xfd-text)', cursor: 'pointer' }}>Default checked</label></div>;
    if (['select', 'radio'].includes(elem.type) && (elem.options || []).length > 0) return wrap(
      <select value={elem.defaultValue || ''} onChange={setVal('defaultValue')} style={propSelectStyle}>
        <option value="">— None —</option>
        {(elem.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
    if (elem.type === 'rich_text' || elem.type === 'textarea') return wrap(<textarea value={elem.defaultValue || ''} onChange={setVal('defaultValue')} rows={3} style={{ ...propInputStyle, resize: 'vertical' }} />);
    return inp('Default value', 'defaultValue');
  };

  return (
    <div style={{ padding: '16px 14px', overflowY: 'auto', flex: 1 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{elem.type}</div>

      {isField && (
        <>
          {inp('Label', 'label')}
          {inp('Field ID (name)', 'name')}
          {inp('Help text', 'helpText')}
          {inp('Tooltip', 'tooltip')}
          {chk('Required', 'required')}
          {/* Case-manager-only authoring: field mapping + conditional rules. */}
          {caseManagerForm && (
            <>
              <div style={{ marginBottom: 10 }}>
                <FieldMappingPicker
                  catalog={fieldCatalog}
                  caseKey={elem.case_field_key}
                  taskKey={elem.task_field_key}
                  fieldName={elem.name}
                  fieldType={elem.type}
                  onChange={({ caseKey, taskKey, unmapped }) => {
                    const next = { ...elem, case_field_key: caseKey, task_field_key: taskKey };
                    if (unmapped) next.no_writeback = true; else delete next.no_writeback;
                    onChange(next);
                  }}
                />
              </div>
              <details style={{ marginBottom: 10, padding: '6px 8px', border: '1px solid var(--xfd-border)', borderRadius: 6, background: 'var(--xfd-input)' }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--xfd-text)' }}>Case Manager Rules</summary>
                <div style={{ marginTop: 8 }}>
                  {csvIn('Required for decisions', 'required_for_decisions', 'Decision values that make this field required (e.g. reject, return).')}
                  {csvIn('Required for roles', 'required_for_roles', 'Case role names — required if the worker has any of these roles.')}
                  {csvIn('Visible for roles', 'visible_for_roles', 'Restrict who sees this field. Empty = visible to everyone.')}
                  {csvIn('Read-only for roles', 'readonly_for_roles', 'Roles for whom this field is non-editable.')}
                </div>
              </details>
            </>
          )}
          {sel('Width', 'width', [{ value: 'full', label: 'Full width' }, { value: 'half', label: 'Half' }, { value: 'third', label: 'Third' }, { value: 'quarter', label: 'Quarter' }])}
          {!['checkbox', 'toggle', 'rating', 'slider', 'range_slider', 'nps', 'opinion_scale', 'matrix', 'signature', 'color', 'map_picker', 'hidden', 'readonly', 'captcha', 'address', 'name', 'file', 'image', 'date', 'time', 'datetime', 'date_range', 'rich_text', 'html', 'image_display', 'video_embed', 'progress_display', 'divider', 'spacer'].includes(elem.type) && inp('Placeholder', 'placeholder')}
          {defaultValueInput()}
          {['text', 'email', 'phone', 'url', 'integer', 'float', 'decimal', 'currency', 'percentage'].includes(elem.type) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
              <div><label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>Prefix</label><input value={elem.prefixText || ''} onChange={setVal('prefixText')} style={propInputStyle} /></div>
              <div><label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>Suffix</label><input value={elem.suffixText || ''} onChange={setVal('suffixText')} style={propInputStyle} /></div>
            </div>
          )}
        </>
      )}

      {elem.type === 'textarea' && (<>{inp('Rows', 'rows', 'number')}{chk('Auto-grow', 'autoGrow')}{chk('Show character count', 'showCharCount')}</>)}
      {elem.type === 'integer' && (<>{inp('Min', 'min', 'number')}{inp('Max', 'max', 'number')}</>)}
      {['float', 'decimal'].includes(elem.type) && (<>{inp('Decimal places', 'decimalPlaces', 'number')}{inp('Min', 'min', 'number')}{inp('Max', 'max', 'number')}{chk('Thousands separator', 'thousandsSeparator')}</>)}
      {elem.type === 'currency' && (<>{inp('Currency code', 'currencyCode')}{inp('Locale', 'locale')}</>)}
      {elem.type === 'percentage' && inp('Decimal places', 'decimalPlaces', 'number')}
      {elem.type === 'password' && (<>{chk('Show strength meter', 'showStrengthMeter')}{chk('Add "Confirm password" field', 'confirmField')}</>)}
      {elem.type === 'url' && chk('Require HTTPS', 'requireHttps')}
      {elem.type === 'toggle' && (<>{inp('On label', 'onLabel')}{inp('Off label', 'offLabel')}{inp('On value', 'onValue')}{inp('Off value', 'offValue')}</>)}
      {elem.type === 'rating' && (<>{inp('Max', 'max', 'number')}{sel('Icon', 'icon', ['star', 'heart', 'thumb'])}{chk('Allow half', 'allowHalf')}</>)}
      {elem.type === 'slider' && (<>{inp('Min', 'min', 'number')}{inp('Max', 'max', 'number')}{inp('Step', 'step', 'number')}{chk('Show current value', 'showValue')}</>)}
      {elem.type === 'range_slider' && (<>{inp('Min', 'min', 'number')}{inp('Max', 'max', 'number')}{inp('Step', 'step', 'number')}</>)}
      {elem.type === 'nps' && (<>{inp('Low label (0)', 'lowLabel')}{inp('High label (10)', 'highLabel')}</>)}
      {elem.type === 'opinion_scale' && (<>{inp('Min', 'min', 'number')}{inp('Max', 'max', 'number')}{inp('Min label', 'minLabel')}{inp('Max label', 'maxLabel')}{chk('Show numbers', 'showNumbers')}</>)}
      {elem.type === 'signature' && (<>{inp('Stroke color', 'strokeColor', 'color')}{inp('Stroke width', 'strokeWidth', 'number')}</>)}
      {['file', 'image'].includes(elem.type) && (<>{inp('Accept (e.g. image/*)', 'accept')}{inp('Max size (MB)', 'maxSizeMb', 'number')}{elem.type === 'file' && chk('Allow multiple files', 'multiple')}{inp('Dropzone label', 'dropzoneLabel')}{elem.type === 'image' && chk('Show preview', 'preview')}{elem.type === 'image' && chk('Enable crop', 'crop')}</>)}
      {elem.type === 'color' && sel('Format', 'format', ['hex', 'rgb', 'hsl'])}
      {elem.type === 'map_picker' && (<>{inp('Default latitude', 'defaultLat', 'number')}{inp('Default longitude', 'defaultLng', 'number')}{inp('Zoom level', 'zoom', 'number')}</>)}
      {elem.type === 'hidden' && (<>{sel('Value mode', 'valueMode', [{ value: 'static', label: 'Static value' }, { value: 'derived', label: 'Derived expression' }])}{elem.valueMode === 'static' ? inp('Static value', 'staticValue') : inp('Expression ({{fieldName}} tokens)', 'derivedExpression')}</>)}
      {elem.type === 'readonly' && (<>{sel('Value mode', 'valueMode', [{ value: 'static', label: 'Static value' }, { value: 'derived', label: 'Derived expression' }])}{elem.valueMode === 'static' ? inp('Static value', 'staticValue') : inp('Expression ({{fieldName}} tokens)', 'derivedExpression')}{sel('Display as', 'displayAs', ['text', 'badge', 'code', 'currency'])}</>)}
      {elem.type === 'html' && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 3 }}>HTML Content</label>
          <textarea value={elem.content || ''} onChange={e => set('content', e.target.value)} rows={6} style={{ ...propInputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
        </div>
      )}
      {elem.type === 'image_display' && (<>{inp('Image URL', 'src')}{inp('Alt text', 'alt')}{sel('Alignment', 'alignment', ['left', 'center', 'right'])}</>)}
      {elem.type === 'video_embed' && (<>{inp('Video URL (YouTube/Vimeo)', 'url')}{inp('Title', 'title')}</>)}
      {elem.type === 'spacer' && inp('Height (px)', 'height', 'number')}
      {elem.type === 'divider' && inp('Label (optional)', 'label')}
      {elem.type === 'collapsible' && (<>{inp('Label', 'label')}{chk('Default open', 'defaultOpen')}</>)}
      {elem.type === 'section' && (<>{inp('Title', 'label')}{inp('Description', 'description')}</>)}
      {elem.type === 'page' && (<>{inp('Step title', 'label')}{inp('Description', 'description')}</>)}
      {elem.type === 'repeating_group' && (<>{inp('Label', 'label')}{inp('Row label (e.g. Item, Person)', 'rowLabel')}{inp('Min rows', 'minRows', 'number')}{inp('Max rows', 'maxRows', 'number')}{inp('Add button label', 'addLabel')}</>)}
      {elem.type === 'columns' && (
        <>
          {inp('Gap (px)', 'gap', 'number')}
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 6 }}>Columns ({(elem.columns || []).length})</label>
            {(elem.columns || []).map((col, i) => (
              <div key={col.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--xfd-dim)', width: 16 }}>{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: 'var(--xfd-border-hover)', display: 'block', marginBottom: 2 }}>Span (1–12)</label>
                  <input
                    type="number" min={1} max={12}
                    value={col.span ?? 6}
                    onChange={e => {
                      const cols = [...elem.columns];
                      cols[i] = { ...cols[i], span: parseInt(e.target.value) || 6 };
                      set('columns', cols);
                    }}
                    style={{ ...propInputStyle, fontSize: 11 }}
                  />
                </div>
                {(elem.columns || []).length > 1 && (
                  <button
                    onClick={() => set('columns', elem.columns.filter((_, j) => j !== i))}
                    style={{ padding: '3px 7px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}
                  >×</button>
                )}
              </div>
            ))}
            <button
              onClick={() => set('columns', [...(elem.columns || []), { id: makeId(), span: 6, elements: [] }])}
              style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }}
            >+ Add column</button>
          </div>
        </>
      )}
      {elem.type === 'tabs' && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 6 }}>Tabs</label>
          {(elem.tabs || []).map((tab, i) => (
            <div key={tab.id} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              <input value={tab.label} onChange={e => { const t = [...elem.tabs]; t[i] = { ...t[i], label: e.target.value }; set('tabs', t); }} style={{ ...propInputStyle, flex: 1, fontSize: 11 }} />
              {elem.tabs.length > 1 && <button onClick={() => set('tabs', elem.tabs.filter((_, j) => j !== i))} style={{ padding: '3px 7px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>×</button>}
            </div>
          ))}
          <button onClick={() => set('tabs', [...(elem.tabs || []), { id: makeId(), label: `Tab ${(elem.tabs || []).length + 1}`, elements: [] }])} style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add tab</button>
        </div>
      )}
      {elem.type === 'matrix' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 4 }}>Rows</label>
            {(elem.rows || []).map((row, i) => (
              <div key={row.id} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input value={row.label} onChange={e => { const r = [...elem.rows]; r[i] = { ...r[i], label: e.target.value }; set('rows', r); }} style={{ ...propInputStyle, flex: 1, fontSize: 11 }} />
                <button onClick={() => set('rows', elem.rows.filter((_, j) => j !== i))} style={{ padding: '3px 7px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>×</button>
              </div>
            ))}
            <button onClick={() => set('rows', [...(elem.rows || []), { id: makeId(), label: `Row ${(elem.rows || []).length + 1}` }])} style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add row</button>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 4 }}>Columns</label>
            {(elem.columns || []).map((col, i) => (
              <div key={col.id} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input value={col.label} onChange={e => { const c = [...elem.columns]; c[i] = { ...c[i], label: e.target.value }; set('columns', c); }} style={{ ...propInputStyle, flex: 1, fontSize: 11 }} />
                <button onClick={() => set('columns', elem.columns.filter((_, j) => j !== i))} style={{ padding: '3px 7px', borderRadius: 4, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 11 }}>×</button>
              </div>
            ))}
            <button onClick={() => set('columns', [...(elem.columns || []), { id: makeId(), label: `Col ${(elem.columns || []).length + 1}` }])} style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add column</button>
          </div>
        </>
      )}

      {hasOptions && <OptionsEditor elem={elem} onChange={onChange} />}
      {hasOptions && <DataSourceEditor elem={elem} allFields={allFields} onChange={onChange} />}

      {isField && !['hidden', 'readonly', 'captcha', 'divider', 'spacer', 'html', 'image_display', 'video_embed', 'progress_display', 'signature'].includes(elem.type) && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', cursor: 'pointer', userSelect: 'none', marginBottom: 4 }}>Validation</summary>
          <ValidationEditor elem={elem} allFields={allFields} onChange={onChange} />
        </details>
      )}

      {!['divider', 'spacer', 'captcha', 'progress_display'].includes(elem.type) && (
        <ConditionalsEditor elem={elem} allFields={allFields} onChange={onChange} />
      )}
    </div>
  );
}

// ─── Main Designer ────────────────────────────────────────────────────────────
export default function XavvyFormDesigner({ formId, onBack, workspaceId }) {
  const [form, setForm] = useState(null);
  const [definition, setDefinition] = useState({ settings: {}, theme: {}, elements: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showAiBuilder, setShowAiBuilder] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCredentialId, setAiCredentialId] = useState('');
  const [aiCredentials, setAiCredentials] = useState([]);
  const [aiInlineKey, setAiInlineKey] = useState('');
  const [aiInlineProvider, setAiInlineProvider] = useState('openai');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [paletteOpen, setPaletteOpen] = useState({});
  // Track active tab per tabs-element (id → tabIndex)
  const [activeTabMap, setActiveTabMap] = useState({});
  // Track expanded state per collapsible-element (id → bool)
  const [expandedMap, setExpandedMap] = useState({});
  const [fieldCatalog, setFieldCatalog] = useState(null);
  const [ruleSets, setRuleSets] = useState([]);

  useEffect(() => {
    if (!workspaceId) { setRuleSets([]); return; }
    API(`/api/rules/workspaces/${workspaceId}/sets`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setRuleSets(d.items || d || []))
      .catch(() => {});
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) { setFieldCatalog(null); return; }
    let cancelled = false;
    API(`/api/casematrix/workspaces/${workspaceId}/field-catalog`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setFieldCatalog(d && (d.case_fields || d.task_fields) ? d : { case_fields: [], task_fields: [] }); })
      .catch(() => { if (!cancelled) setFieldCatalog({ case_fields: [], task_fields: [] }); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const dragState = useRef(null);
  const autoSaveTimer = useRef(null);
  const definitionRef = useRef(definition);
  definitionRef.current = definition;

  useEffect(() => {
    if (!formId) return;
    API(`/xavvy-forms/${formId}`).then(r => r.json()).then(d => {
      setForm(d);
      const defn = typeof d.definition === 'string' ? JSON.parse(d.definition) : (d.definition || {});
      const init = {
        settings: { title: d.name, description: '', submitLabel: 'Submit', successMessage: 'Thank you!', redirectUrl: '', honeypot: true, multiStep: false, showProgressBar: false, autoSave: true, prefillFromUrl: true, customCss: '', ...defn.settings },
        theme: { primaryColor: '#6366f1', backgroundColor: 'var(--xfd-input)', cardBackground: 'var(--xfd-panel)', textColor: 'var(--xfd-text)', borderRadius: '10px', fontFamily: 'Inter, sans-serif', layout: 'card', ...defn.theme },
        elements: defn.elements || [],
      };
      setDefinition(init);
      setHistory([JSON.stringify(init)]);
      setHistoryIndex(0);
    });
  }, [formId]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
      if (e.key === 'Delete' && selectedId && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        deleteElement(selectedId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const pushHistory = useCallback((defn) => {
    const snap = JSON.stringify(defn);
    setHistory(h => { const trimmed = h.slice(0, historyIndex + 1); return [...trimmed, snap]; });
    setHistoryIndex(i => i + 1);
  }, [historyIndex]);

  const undo = () => {
    if (historyIndex <= 0) return;
    const newIdx = historyIndex - 1;
    setHistoryIndex(newIdx);
    setDefinition(JSON.parse(history[newIdx]));
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const newIdx = historyIndex + 1;
    setHistoryIndex(newIdx);
    setDefinition(JSON.parse(history[newIdx]));
  };

  const updateDefinition = (newDefn) => {
    setDefinition(newDefn);
    pushHistory(newDefn);
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => doSave(newDefn, true), 3000);
  };

  const doSave = async (defn = definitionRef.current, silent = false) => {
    if (!silent) setSaving(true);
    const payload = { name: form?.name || defn.settings?.title || 'Untitled', definition: defn };
    await API(`/xavvy-forms/${formId}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (!silent) { setSaving(false); setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000); }
  };

  const handlePublish = async () => {
    await doSave();
    const res = await API(`/xavvy-forms/${formId}`, { method: 'PUT', body: JSON.stringify({ status: form?.status === 'published' ? 'draft' : 'published' }) });
    const updated = await res.json();
    setForm(f => ({ ...f, status: updated.status }));
  };

  const openAiBuilder = async () => {
    setShowAiBuilder(true);
    setAiError('');
    if (aiCredentials.length === 0) {
      try {
        const res = await API('/credentials');
        const all = await res.json();
        const aiCreds = (Array.isArray(all) ? all : []).filter(c => ['openai', 'gemini', 'openai_compatible'].includes((c.service_type || '').toLowerCase()));
        setAiCredentials(aiCreds);
        if (aiCreds.length > 0 && !aiCredentialId) setAiCredentialId(aiCreds[0].id);
      } catch (_) {}
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    const hasCredential = !!aiCredentialId;
    const hasInlineKey = !!aiInlineKey.trim();
    if (!hasCredential && !hasInlineKey) {
      setAiError('Enter a saved credential or paste an API key to continue.');
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const body = hasCredential
        ? { credential_id: aiCredentialId, prompt: aiPrompt }
        : { api_key: aiInlineKey.trim(), provider: aiInlineProvider, prompt: aiPrompt };
      const res = await API('/ai/generate-form', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'AI generation failed');
      }
      const { definition: generated } = await res.json();
      const merged = {
        ...definition,
        settings: { ...definition.settings, ...(generated.settings || {}) },
        elements: generated.elements || [],
      };
      updateDefinition(merged);
      setShowAiBuilder(false);
      setAiPrompt('');
    } catch (e) {
      setAiError(e.message || 'Generation failed');
    }
    setAiLoading(false);
  };

  // Collect all named fields for conditionals / data source depends-on
  const allFields = [];
  const collectFields = (elems) => {
    (elems || []).forEach(e => {
      if (e.name) allFields.push(e);
      if (e.elements) collectFields(e.elements);
      if (e.columns) e.columns.forEach(c => collectFields(c.elements));
      if (e.tabs) e.tabs.forEach(t => collectFields(t.elements));
    });
  };
  collectFields(definition.elements);

  // ── Tree helpers ────────────────────────────────────────────────────────────

  const removeById = (elems, id) => {
    return elems.filter(e => e.id !== id).map(e => {
      if (e.elements) return { ...e, elements: removeById(e.elements, id) };
      if (e.columns) return { ...e, columns: e.columns.map(c => ({ ...c, elements: removeById(c.elements, id) })) };
      if (e.tabs) return { ...e, tabs: e.tabs.map(t => ({ ...t, elements: removeById(t.elements, id) })) };
      return e;
    });
  };

  const updateById = (elems, id, updater) => {
    return elems.map(e => {
      if (e.id === id) return updater(e);
      if (e.elements) return { ...e, elements: updateById(e.elements, id, updater) };
      if (e.columns) return { ...e, columns: e.columns.map(c => ({ ...c, elements: updateById(c.elements, id, updater) })) };
      if (e.tabs) return { ...e, tabs: e.tabs.map(t => ({ ...t, elements: updateById(t.elements, id, updater) })) };
      return e;
    });
  };

  const deleteElement = (id) => {
    const newElems = removeById(definition.elements, id);
    updateDefinition({ ...definition, elements: newElems });
    if (selectedId === id) setSelectedId(null);
  };

  const updateElement = (updated) => {
    const newElems = updateById(definition.elements, updated.id, () => updated);
    updateDefinition({ ...definition, elements: newElems });
  };

  // ── Drag handlers ──────────────────────────────────────────────────────────

  const handlePaletteDragStart = (type) => { dragState.current = { source: 'palette', type }; };
  const handleElementDragStart = (elem) => { dragState.current = { source: 'canvas', elem }; };

  // insertFn(newElem, rootElems) => newRootElems
  const handleDrop = (insertFn) => {
    if (!dragState.current) return;
    const ds = dragState.current;
    dragState.current = null;
    const currentDef = definitionRef.current;

    if (ds.source === 'palette') {
      const newElem = defaultElement(ds.type);
      const newElems = insertFn(newElem, currentDef.elements);
      setSelectedId(newElem.id);
      updateDefinition({ ...currentDef, elements: newElems });
    } else if (ds.source === 'canvas') {
      const elem = ds.elem;
      const withoutElems = removeById(currentDef.elements, elem.id);
      const newElems = insertFn(elem, withoutElems);
      updateDefinition({ ...currentDef, elements: newElems });
    }
  };

  // ── Canvas renderer ────────────────────────────────────────────────────────
  // makeInsertFn(afterId) returns an insertFn(newElem, rootElems) => newRootElems
  // that inserts newElem into the correct position in the full tree

  const renderElements = (elems, makeInsertFn, depth = 0) => {
    const isEmpty = !elems || elems.length === 0;

    if (isEmpty) {
      return (
        <DropZone
          key="empty"
          onDrop={() => handleDrop(makeInsertFn(null))}
        />
      );
    }

    return (
      <>
        <DropZone key="dz_top" onDrop={() => handleDrop(makeInsertFn(null))} />
        {elems.map((elem) => (
          <React.Fragment key={elem.id}>
            <div
              draggable
              onDragStart={(e) => { e.stopPropagation(); handleElementDragStart(elem); }}
            >
              {elem.type === 'columns' ? (
                <div
                  style={{ border: selectedId === elem.id ? '2px solid var(--accent-color, #6366f1)' : '1px dashed var(--xfd-border)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--xfd-subtle)', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setSelectedId(elem.id); }}
                >
                  <div style={{ fontSize: 11, color: 'var(--xfd-dim)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>⊞ Columns</span>
                    <button onClick={e => { e.stopPropagation(); deleteElement(elem.id); }} style={{ padding: '1px 5px', borderRadius: 3, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>×</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: (elem.columns || []).map(c => `${c.span ?? 1}fr`).join(' '), gap: elem.gap || 16 }}>
                    {(elem.columns || []).map((col, colIdx) => {
                      const makeColInsertFn = (afterId) => (newElem, rootElems) =>
                        updateById(rootElems, elem.id, (ce) => ({
                          ...ce,
                          columns: ce.columns.map((c, ci) =>
                            ci === colIdx ? { ...c, elements: insertAfterInArray(c.elements, afterId, newElem) } : c
                          ),
                        }));
                      return (
                        <ColumnDropArea
                          key={col.id}
                          col={col}
                          colIdx={colIdx}
                          makeColInsertFn={makeColInsertFn}
                          handleDrop={handleDrop}
                          renderElements={renderElements}
                          depth={depth}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : elem.type === 'tabs' ? (
                <div
                  style={{ border: selectedId === elem.id ? '2px solid var(--accent-color, #6366f1)' : '1px dashed var(--xfd-border)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--xfd-subtle)', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setSelectedId(elem.id); }}
                >
                  <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderBottom: '1px solid var(--xfd-border)', paddingBottom: 0, alignItems: 'center' }}>
                    {(elem.tabs || []).map((tab, ti) => {
                      const isActive = (activeTabMap[elem.id] ?? 0) === ti;
                      return (
                        <span
                          key={tab.id}
                          onClick={e => { e.stopPropagation(); setActiveTabMap(m => ({ ...m, [elem.id]: ti })); }}
                          style={{ fontSize: 12, color: isActive ? 'var(--accent-color, #6366f1)' : 'var(--xfd-dim)', borderBottom: isActive ? '2px solid var(--accent-color, #6366f1)' : '2px solid transparent', padding: '4px 12px', cursor: 'pointer', marginBottom: -1, fontWeight: isActive ? 600 : 400, userSelect: 'none' }}
                        >
                          {tab.label}
                        </span>
                      );
                    })}
                    <button onClick={e => { e.stopPropagation(); deleteElement(elem.id); }} style={{ marginLeft: 'auto', padding: '1px 5px', borderRadius: 3, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>×</button>
                  </div>
                  {(() => {
                    const activeTabIdx = activeTabMap[elem.id] ?? 0;
                    const activeTab = (elem.tabs || [])[activeTabIdx];
                    if (!activeTab) return null;
                    const makeTabInsertFn = (afterId) => (newElem, rootElems) =>
                      updateById(rootElems, elem.id, (te) => ({
                        ...te,
                        tabs: te.tabs.map((t, ti) =>
                          ti === activeTabIdx ? { ...t, elements: insertAfterInArray(t.elements, afterId, newElem) } : t
                        ),
                      }));
                    return (
                      <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); const lastId = activeTab.elements[activeTab.elements.length - 1]?.id ?? null; handleDrop(makeTabInsertFn(lastId)); }}
                        style={{ minHeight: 48, padding: 4 }}
                      >
                        {renderElements(activeTab.elements, makeTabInsertFn, depth + 1)}
                      </div>
                    );
                  })()}
                </div>
              ) : (elem.type === 'collapsible' || elem.type === 'section') ? (
                <div
                  style={{ border: selectedId === elem.id ? '2px solid var(--accent-color, #6366f1)' : '1px dashed var(--xfd-border)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--xfd-subtle)' }}
                >
                  <div
                    style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); setSelectedId(elem.id); }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={e => { e.stopPropagation(); setExpandedMap(m => ({ ...m, [elem.id]: !(m[elem.id] ?? true) })); }}
                    >
                      <span style={{ fontSize: 10, color: 'var(--xfd-dim)', transition: 'transform 0.15s', display: 'inline-block', transform: (expandedMap[elem.id] ?? true) ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      {elem.type === 'collapsible' ? elem.label || 'Collapsible' : `§ ${elem.label || 'Section'}`}
                    </div>
                    <button onClick={e => { e.stopPropagation(); deleteElement(elem.id); }} style={{ padding: '1px 5px', borderRadius: 3, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>×</button>
                  </div>
                  {(expandedMap[elem.id] ?? true) && (
                    <div
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); const lastId = elem.elements[elem.elements.length - 1]?.id ?? null; handleDrop((newElem, rootElems) => updateById(rootElems, elem.id, (ce) => ({ ...ce, elements: insertAfterInArray(ce.elements, lastId, newElem) }))); }}
                      style={{ minHeight: 32, paddingTop: 4 }}
                    >
                      {renderElements(
                        elem.elements,
                        (afterId) => (newElem, rootElems) =>
                          updateById(rootElems, elem.id, (ce) => ({
                            ...ce,
                            elements: insertAfterInArray(ce.elements, afterId, newElem),
                          })),
                        depth + 1
                      )}
                    </div>
                  )}
                </div>
              ) : elem.type === 'page' ? (
                <div style={{ border: selectedId === elem.id ? '2px solid var(--accent-color, #6366f1)' : '2px dashed var(--xfd-border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--xfd-subtle)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--xfd-dim)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setSelectedId(elem.id); }}>
                    Step: {elem.label || 'Page'}
                  </div>
                  <div
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const lastId = elem.elements[elem.elements.length - 1]?.id ?? null; handleDrop((newElem, rootElems) => updateById(rootElems, elem.id, (ce) => ({ ...ce, elements: insertAfterInArray(ce.elements, lastId, newElem) }))); }}
                  >
                    {renderElements(
                      elem.elements,
                      (afterId) => (newElem, rootElems) =>
                        updateById(rootElems, elem.id, (ce) => ({
                          ...ce,
                          elements: insertAfterInArray(ce.elements, afterId, newElem),
                        })),
                      depth + 1
                    )}
                  </div>
                </div>
              ) : elem.type === 'repeating_group' ? (
                <div
                  style={{ border: selectedId === elem.id ? '2px solid var(--accent-color, #6366f1)' : '1px dashed var(--xfd-border)', borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--xfd-subtle)', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setSelectedId(elem.id); }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                    ↻ {elem.label || 'Repeating Group'}
                    <button onClick={e => { e.stopPropagation(); deleteElement(elem.id); }} style={{ padding: '1px 5px', borderRadius: 3, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer', fontSize: 10 }}>×</button>
                  </div>
                  <div
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const lastId = elem.elements[elem.elements.length - 1]?.id ?? null; handleDrop((newElem, rootElems) => updateById(rootElems, elem.id, (ce) => ({ ...ce, elements: insertAfterInArray(ce.elements, lastId, newElem) }))); }}
                  >
                    {renderElements(
                      elem.elements,
                      (afterId) => (newElem, rootElems) =>
                        updateById(rootElems, elem.id, (ce) => ({
                          ...ce,
                          elements: insertAfterInArray(ce.elements, afterId, newElem),
                        })),
                      depth + 1
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--accent-color, #6366f1)', marginTop: 6 }}>+ {elem.addLabel || 'Add row'}</div>
                </div>
              ) : (
                <ElementPreview
                  elem={elem}
                  selected={selectedId === elem.id}
                  onClick={(e) => setSelectedId(e.id)}
                  onDelete={deleteElement}
                  dragHandleProps={{ onDragStart: (e) => { e.stopPropagation(); handleElementDragStart(elem); } }}
                  depth={depth}
                />
              )}
            </div>
            <DropZone key={`dz_after_${elem.id}`} onDrop={() => handleDrop(makeInsertFn(elem.id))} />
          </React.Fragment>
        ))}
      </>
    );
  };

  // ── Settings modal ─────────────────────────────────────────────────────────
  // Defined as JSX (not a sub-component) so React doesn't unmount/remount on re-render, which would lose focus.
  const _s = definition.settings;
  const _setS = (k, v) => updateDefinition({ ...definition, settings: { ..._s, [k]: v } });
  const settingsModalJSX = showSettings && (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSettings(false)}>
      <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 12, width: 560, maxHeight: '80vh', overflow: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>Form Settings <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', color: 'var(--xfd-dim)', fontSize: 18, cursor: 'pointer' }}>×</button></div>
        {/* Form usage context — drives which advanced options surface in the field editor */}
        <div style={{ marginBottom: 16, padding: '10px 12px', background: 'var(--xfd-input)', border: '1px solid var(--xfd-border)', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={!!form?.is_case_manager_form}
              onChange={async (e) => {
                const next = e.target.checked;
                setForm(f => ({ ...f, is_case_manager_form: next }));
                await API(`/xavvy-forms/${formId}`, { method: 'PUT', body: JSON.stringify({ is_case_manager_form: next }) });
              }}
              id="s_is_case_manager_form"
            />
            <label htmlFor="s_is_case_manager_form" style={{ fontSize: 13, fontWeight: 600, color: 'var(--xfd-text)', cursor: 'pointer' }}>
              Use this form in xavvy Case Manager
            </label>
          </div>
          <div style={{ fontSize: 11, color: 'var(--xfd-dim)', marginTop: 4, marginLeft: 24 }}>
            When enabled, fields can map to case / task custom fields and the editor surfaces case-manager-specific options.
          </div>
        </div>
        {[['title', 'Form Title'], ['description', 'Description'], ['submitLabel', 'Submit Button Label'], ['successMessage', 'Success Message'], ['redirectUrl', 'Redirect URL (after submit)']].map(([k, label]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-dim)', marginBottom: 4 }}>{label}</label>
            <input value={_s[k] || ''} onChange={e => _setS(k, e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }} />
          </div>
        ))}
        {[['multiStep', 'Multi-step form'], ['showProgressBar', 'Show progress bar'], ['autoSave', 'Auto-save (localStorage)'], ['allowDraftSave', 'Show "Save Draft" button'], ['prefillFromUrl', 'Pre-fill from URL params'], ['honeypot', 'Honeypot spam protection'], ['embedAllowed', 'Allow iframe embed']].map(([k, label]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={!!_s[k]} onChange={e => _setS(k, e.target.checked)} id={`s_${k}`} />
            <label htmlFor={`s_${k}`} style={{ fontSize: 13, color: 'var(--xfd-text)', cursor: 'pointer' }}>{label}</label>
          </div>
        ))}
        {[['submissionLimit', 'Max submissions (blank = unlimited)'], ['notifyOwnerEmail', 'Notify owner email'], ['notifySubmitterField', 'Notify submitter (field name with email)']].map(([k, label]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-dim)', marginBottom: 4 }}>{label}</label>
            <input value={_s[k] || ''} onChange={e => _setS(k, e.target.value || null)} style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }} />
          </div>
        ))}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-dim)', marginBottom: 4 }}>Custom CSS</label>
          <textarea value={_s.customCss || ''} onChange={e => _setS('customCss', e.target.value)} rows={4} style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 12, fontFamily: 'monospace', resize: 'vertical' }} />
        </div>
        {ruleSets.length > 0 && (
          <div style={{ marginBottom: 12, paddingTop: 12, borderTop: '1px solid var(--xfd-border)' }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-dim)', marginBottom: 4 }}>
              Validation Rule Set <span style={{ color: '#8b5cf6', fontWeight: 600 }}>(Rules Engine)</span>
            </label>
            <select
              value={form?.validation_rule_set_id || ''}
              onChange={async e => {
                const val = e.target.value || null;
                setForm(f => ({ ...f, validation_rule_set_id: val }));
                await API(`/xavvy-forms/${formId}`, { method: 'PUT', body: JSON.stringify({ validation_rule_set_id: val }) });
              }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }}
            >
              <option value="">None (client-side validation only)</option>
              {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
            </select>
            <div style={{ fontSize: 11, color: 'var(--xfd-dim)', marginTop: 4 }}>
              When set, the server evaluates this rule set on each form submission and blocks it if a <code>validation_error</code> action fires.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Theme panel ────────────────────────────────────────────────────────────
  const _t = definition.theme;
  const _setT = (k, v) => updateDefinition({ ...definition, theme: { ..._t, [k]: v } });
  const _colorField = (label, key) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <label style={{ fontSize: 12, color: 'var(--xfd-dim)', flex: 1 }}>{label}</label>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input type="color" value={_t[key] || '#000000'} onChange={e => _setT(key, e.target.value)} style={{ width: 32, height: 28, borderRadius: 4, border: 'none', cursor: 'pointer' }} />
        <input value={_t[key] || ''} onChange={e => _setT(key, e.target.value)} style={{ width: 80, padding: '5px 6px', borderRadius: 4, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 11, fontFamily: 'monospace' }} />
      </div>
    </div>
  );
  const themePanelJSX = showTheme && (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowTheme(false)}>
      <div style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 12, width: 380, maxHeight: '80vh', overflow: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>Theme <button onClick={() => setShowTheme(false)} style={{ background: 'none', border: 'none', color: 'var(--xfd-dim)', fontSize: 18, cursor: 'pointer' }}>×</button></div>
        {_colorField('Primary / accent', 'primaryColor')}
        {_colorField('Form background', 'backgroundColor')}
        {_colorField('Card background', 'cardBackground')}
        {_colorField('Text color', 'textColor')}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--xfd-dim)', display: 'block', marginBottom: 4 }}>Border radius</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="range" min={0} max={24} value={parseInt(_t.borderRadius || '10')} onChange={e => _setT('borderRadius', `${e.target.value}px`)} style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: 'var(--xfd-muted)', width: 36 }}>{_t.borderRadius || '10px'}</span>
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--xfd-dim)', display: 'block', marginBottom: 4 }}>Font family</label>
          <select value={_t.fontFamily || 'Inter, sans-serif'} onChange={e => _setT('fontFamily', e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }}>
            {['Inter, sans-serif', 'Roboto, sans-serif', 'Open Sans, sans-serif', 'DM Sans, sans-serif', 'system-ui, sans-serif'].map(f => <option key={f} value={f}>{f.split(',')[0]}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--xfd-dim)', display: 'block', marginBottom: 4 }}>Layout style</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {['card', 'flat', 'minimal'].map(l => <button key={l} onClick={() => _setT('layout', l)} style={{ flex: 1, padding: '7px', borderRadius: 6, border: '1px solid var(--xfd-border)', background: _t.layout === l ? 'var(--accent-color, #6366f1)' : 'none', color: _t.layout === l ? '#fff' : 'var(--xfd-muted)', cursor: 'pointer', fontSize: 12, textTransform: 'capitalize' }}>{l}</button>)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreviewElements = (elems) =>
    (elems || []).map(elem => {
      if (elem.type === 'columns') {
        return (
          <div key={elem.id} style={{ display: 'grid', gridTemplateColumns: (elem.columns || []).map(c => `${c.span ?? 1}fr`).join(' '), gap: elem.gap || 16, marginBottom: 8 }}>
            {(elem.columns || []).map(col => (
              <div key={col.id}>{renderPreviewElements(col.elements)}</div>
            ))}
          </div>
        );
      }
      if (elem.type === 'tabs') {
        return <PreviewTabsBlock key={elem.id} elem={elem} renderFn={renderPreviewElements} />;
      }
      if (elem.type === 'collapsible' || elem.type === 'section') {
        return (
          <div key={elem.id} style={{ marginBottom: 8, padding: '8px 12px', border: '1px dashed var(--xfd-border)', borderRadius: 6 }}>
            {elem.label && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 6 }}>{elem.type === 'section' ? `§ ${elem.label}` : elem.label}</div>}
            {renderPreviewElements(elem.elements)}
          </div>
        );
      }
      if (elem.type === 'page') {
        return (
          <div key={elem.id} style={{ marginBottom: 16 }}>
            {elem.label && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--xfd-dim)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{elem.label}</div>}
            {renderPreviewElements(elem.elements)}
          </div>
        );
      }
      if (elem.type === 'repeating_group') {
        return (
          <div key={elem.id} style={{ marginBottom: 8, padding: '8px 12px', border: '1px dashed var(--xfd-border)', borderRadius: 6 }}>
            {elem.label && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--xfd-muted)', marginBottom: 6 }}>↻ {elem.label}</div>}
            {renderPreviewElements(elem.elements)}
          </div>
        );
      }
      return <ElementPreview key={elem.id} elem={elem} selected={false} onClick={() => {}} onDelete={() => {}} dragHandleProps={{}} />;
    });

  if (!form) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--xfd-dim)' }}>Loading…</div>;

  const selectedElem = selectedId ? (() => {
    let found = null;
    const search = (elems) => {
      for (const e of (elems || [])) {
        if (e.id === selectedId) { found = e; return; }
        if (e.elements) search(e.elements);
        if (e.columns) e.columns.forEach(c => search(c.elements));
        if (e.tabs) e.tabs.forEach(t => search(t.elements));
      }
    };
    search(definition.elements);
    return found;
  })() : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--xfd-bg)', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden' }}>
      {settingsModalJSX}
      {themePanelJSX}
      {previewMode && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}
          onClick={() => setPreviewMode(false)}
        >
          <div
            style={{ position: 'relative', width: '100%', maxWidth: 680, background: 'var(--xfd-panel)', borderRadius: 16, padding: 32, boxShadow: '0 25px 60px rgba(0,0,0,0.5)', marginBottom: 32 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 11, color: 'var(--xfd-dim)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Preview</span>
              <button onClick={() => setPreviewMode(false)} style={{ background: 'none', border: 'none', color: 'var(--xfd-dim)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {definition.settings?.title && <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--xfd-text)', marginBottom: 6, marginTop: 0 }}>{definition.settings.title}</h2>}
            {definition.settings?.description && <p style={{ color: 'var(--xfd-muted)', marginBottom: 20, marginTop: 0, fontSize: 14 }}>{definition.settings.description}</p>}
            {renderPreviewElements(definition.elements)}
            {definition.elements.length > 0 && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--xfd-border)' }}>
                <button disabled style={{ padding: '10px 28px', borderRadius: 8, border: 'none', background: definition.theme?.primaryColor || '#6366f1', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'default', opacity: 0.8 }}>
                  {definition.settings?.submitLabel || 'Submit'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', height: 52, borderBottom: '1px solid var(--xfd-border)', background: 'var(--xfd-panel)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 5 }}>← Back</button>
        <input
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          style={{ background: 'none', border: 'none', color: 'var(--xfd-text)', fontSize: 15, fontWeight: 600, outline: 'none', flex: 1 }}
        />
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
          <button onClick={() => undo()} disabled={historyIndex <= 0} title="Undo (Ctrl+Z)" style={{ padding: '5px 9px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 12, opacity: historyIndex <= 0 ? 0.4 : 1 }}>↩</button>
          <button onClick={() => redo()} disabled={historyIndex >= history.length - 1} title="Redo (Ctrl+Y)" style={{ padding: '5px 9px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 12, opacity: historyIndex >= history.length - 1 ? 0.4 : 1 }}>↪</button>
          <button onClick={openAiBuilder} title="Generate form with AI" style={{ padding: '5px 11px', borderRadius: 5, border: '1px solid rgba(99,102,241,0.5)', background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', cursor: 'pointer', fontSize: 13 }}>✨ AI</button>
          <button onClick={() => setShowSettings(true)} style={{ padding: '5px 11px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>⚙ Settings</button>
          <button onClick={() => setShowTheme(true)} style={{ padding: '5px 11px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>🎨 Theme</button>
          <button onClick={() => setPreviewMode(p => !p)} style={{ padding: '5px 11px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: previewMode ? 'rgba(99,102,241,0.2)' : 'none', color: previewMode ? 'var(--accent-color, #6366f1)' : 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>👁 Preview</button>
          <button onClick={() => doSave()} disabled={saving} style={{ padding: '5px 14px', borderRadius: 5, border: '1px solid var(--xfd-border)', background: 'none', color: saveMsg ? '#22c55e' : 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>{saving ? '…' : saveMsg || '💾 Save'}</button>
          <button onClick={handlePublish} style={{ padding: '5px 14px', borderRadius: 5, border: 'none', background: form.status === 'published' ? '#f59e0b' : 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{form.status === 'published' ? '↓ Unpublish' : '🚀 Publish'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Left palette ── */}
        <div style={{ width: 200, borderRight: '1px solid var(--xfd-border)', background: 'var(--xfd-panel)', overflowY: 'auto', flexShrink: 0 }}>
          {PALETTE.map(group => (
            <div key={group.group}>
              <div
                onClick={() => setPaletteOpen(p => ({ ...p, [group.group]: !p[group.group] }))}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', color: 'var(--xfd-dim)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, userSelect: 'none', borderBottom: '1px solid var(--xfd-border)' }}
              >
                <span>{group.group}</span>
                <span style={{ fontSize: 9 }}>{paletteOpen[group.group] === false ? '▶' : '▾'}</span>
              </div>
              {paletteOpen[group.group] !== false && (
                <div style={{ padding: '4px 0' }}>
                  {group.items.map(item => (
                    <div
                      key={item.type}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); handlePaletteDragStart(item.type); }}
                      onClick={() => {
                        const newElem = defaultElement(item.type);
                        updateDefinition({ ...definition, elements: [...definition.elements, newElem] });
                        setSelectedId(newElem.id);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'grab', fontSize: 12, color: 'var(--xfd-muted)', userSelect: 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ fontSize: 11, width: 16, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                      <span style={{ fontSize: 12 }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Canvas ── */}
        <div
          style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', background: 'var(--xfd-bg)' }}
          onClick={() => setSelectedId(null)}
        >
          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            <input
              value={definition.settings?.title || ''}
              onChange={e => updateDefinition({ ...definition, settings: { ...definition.settings, title: e.target.value } })}
              placeholder="Form title…"
              style={{ fontSize: 22, fontWeight: 700, background: 'none', border: 'none', color: 'var(--xfd-text)', outline: 'none', width: '100%', marginBottom: 8 }}
            />
            <input
              value={definition.settings?.description || ''}
              onChange={e => updateDefinition({ ...definition, settings: { ...definition.settings, description: e.target.value } })}
              placeholder="Description (optional)…"
              style={{ fontSize: 13, background: 'none', border: 'none', color: 'var(--xfd-dim)', outline: 'none', width: '100%', marginBottom: 20 }}
            />
            {renderElements(
              definition.elements,
              (afterId) => (newElem, elems) => insertAfterInArray(elems, afterId, newElem)
            )}
            {definition.elements.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--xfd-border)', display: 'flex', gap: 8 }}>
                <button disabled style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: definition.theme?.primaryColor || 'var(--accent-color, #6366f1)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'default', opacity: 0.7 }}>
                  {definition.settings?.submitLabel || 'Submit'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Properties panel ── */}
        <div style={{ width: 320, borderLeft: '1px solid var(--xfd-border)', background: 'var(--xfd-panel)', overflowY: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          <PropsPanel elem={selectedElem} allFields={allFields} onChange={updateElement} fieldCatalog={fieldCatalog} caseManagerForm={!!form?.is_case_manager_form} />
        </div>

      </div>

      {/* ── AI Form Builder Modal ── */}
      {showAiBuilder && (
        <div onClick={() => setShowAiBuilder(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--xfd-panel)', border: '1px solid var(--xfd-border)', borderRadius: 14, width: 'min(520px, 92vw)', padding: '24px', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--xfd-text)' }}>✨ AI Form Builder</div>
                <div style={{ fontSize: 12, color: 'var(--xfd-dim)', marginTop: 2 }}>Describe your form and AI will generate all the fields</div>
              </div>
              <button onClick={() => setShowAiBuilder(false)} style={{ background: 'none', border: 'none', color: 'var(--xfd-dim)', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            {/* Credential row: saved picker OR inline key entry */}
            {aiCredentials.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-muted)', marginBottom: 6 }}>AI Credential</label>
                <select
                  value={aiCredentialId}
                  onChange={e => setAiCredentialId(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }}
                >
                  {aiCredentials.map(c => <option key={c.id} value={c.id}>{c.name} ({c.service_type})</option>)}
                </select>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-muted)', marginBottom: 6 }}>Provider</label>
                    <select
                      value={aiInlineProvider}
                      onChange={e => setAiInlineProvider(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13 }}
                    >
                      <option value="openai">OpenAI</option>
                      <option value="gemini">Gemini</option>
                    </select>
                  </div>
                  <div style={{ flex: 3 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-muted)', marginBottom: 6 }}>API Key</label>
                    <input
                      type="password"
                      value={aiInlineKey}
                      onChange={e => setAiInlineKey(e.target.value)}
                      placeholder={aiInlineProvider === 'gemini' ? 'AIza…' : 'sk-…'}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--xfd-border-hover)' }}>Key is used only for this request and never stored.</div>
              </div>
            )}

            {/* Prompt */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--xfd-muted)', marginBottom: 6 }}>Describe your form</label>
              <textarea
                autoFocus
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAiGenerate(); }}
                placeholder="e.g. A job application form with name, email, phone, resume upload, work experience, and a cover letter text area"
                rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'var(--xfd-input)', color: 'var(--xfd-text)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
            {aiError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{aiError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAiBuilder(false)} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid var(--xfd-border)', background: 'none', color: 'var(--xfd-muted)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleAiGenerate}
                disabled={!aiPrompt.trim() || aiLoading || (aiCredentials.length === 0 && !aiInlineKey.trim())}
                style={{ padding: '8px 20px', borderRadius: 7, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (!aiPrompt.trim() || aiLoading || (aiCredentials.length === 0 && !aiInlineKey.trim())) ? 0.6 : 1 }}
              >
                {aiLoading ? 'Generating…' : '✨ Generate Form'}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--xfd-border-hover)', textAlign: 'right' }}>Ctrl+Enter to generate</div>
          </div>
        </div>
      )}
    </div>
  );
}
